import React from 'react';
import {
  arrayBuilderItemFirstPageTitleUI,
  arrayBuilderYesNoSchema,
  arrayBuilderYesNoUI,
  checkboxGroupSchema,
  checkboxGroupUI,
  selectSchema,
  selectUI,
  textSchema,
  textUI,
  titleUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import { arrayBuilderPages } from 'platform/forms-system/src/js/patterns/array-builder';

import {
  BODY_PART_LABELS,
  FORM_10_8678,
  MAX_BENEFIT_ITEMS,
} from '../definitions/constants';
import VhaFacilityAutosuggestField from '../components/VhaFacilityAutosuggestField';
import {
  getVhaMedicalFacilityLabel,
  isOtherVhaMedicalFacility,
  VHA_FACILITY_STATE_SCHEMA_KEY,
} from '../definitions/facilities';
import {
  isVhaFacilityStateCode,
  normalizeVhaFacilityState,
  VHA_FACILITY_STATE_LABELS,
  VHA_FACILITY_STATE_VALUES,
} from '../definitions/facilityStates';
import { getVhaMedicalFacilityOptions } from '../services/facilities';

const { VHA_MEDICAL_FACILITY, DEVICE_APPLIANCE_MEDICATION_DETAILS } =
  FORM_10_8678;

const deviceDetails = DEVICE_APPLIANCE_MEDICATION_DETAILS;

const getArrayBuilderItem = (formDataOrItem, index) => {
  const itemIndex = Number(index);
  if (
    Number.isInteger(itemIndex) &&
    formDataOrItem?.[deviceDetails.ARRAY_PATH]?.[itemIndex]
  ) {
    return formDataOrItem[deviceDetails.ARRAY_PATH][itemIndex];
  }
  return formDataOrItem;
};

export const isOtherMedicalFacility = item =>
  isOtherVhaMedicalFacility(item?.issuingFacility);

export const validateOtherMedicalFacility = (errors, fieldData) => {
  if (
    isOtherMedicalFacility(fieldData) &&
    !fieldData?.issuingFacilityOther?.trim() &&
    errors?.issuingFacilityOther?.addError
  ) {
    errors.issuingFacilityOther.addError(
      deviceDetails.ISSUING_FACILITY_OTHER_ERROR,
    );
  }
};

export const updateIssuingFacilitySchema = (
  formData,
  schema,
  _uiSchema,
  index,
) => {
  const item = getArrayBuilderItem(formData, index);
  const facilityState = normalizeVhaFacilityState(item?.issuingFacilityState);
  if (!isVhaFacilityStateCode(facilityState)) {
    return schema[VHA_FACILITY_STATE_SCHEMA_KEY]
      ? {
          ...schema,
          [VHA_FACILITY_STATE_SCHEMA_KEY]: undefined,
        }
      : schema;
  }
  if (schema[VHA_FACILITY_STATE_SCHEMA_KEY] === facilityState) {
    return schema;
  }

  return {
    ...schema,
    [VHA_FACILITY_STATE_SCHEMA_KEY]: facilityState,
  };
};

export const updateDeviceItemFormData = (
  previousItem = {},
  nextFormData = {},
  index,
) => {
  const itemIndex = Number(index);
  const items = Array.isArray(nextFormData?.[deviceDetails.ARRAY_PATH])
    ? [...nextFormData[deviceDetails.ARRAY_PATH]]
    : [];
  if (!Number.isInteger(itemIndex) || itemIndex < 0 || !items[itemIndex]) {
    return nextFormData;
  }

  const nextItem = {
    ...items[itemIndex],
  };

  const previousState = previousItem?.issuingFacilityState || '';
  const nextState = nextItem.issuingFacilityState || '';
  if (previousState && previousState !== nextState) {
    nextItem.issuingFacility = undefined;
    nextItem.issuingFacilityOther = undefined;
  }
  items[itemIndex] = nextItem;
  return {
    ...nextFormData,
    [deviceDetails.ARRAY_PATH]: items,
  };
};

const ItemDetailsDescription = () => (
  <div className="vads-u-font-weight--normal vads-u-margin-top--0 vads-u-margin-bottom--0">
    {deviceDetails.ITEM_DETAILS_DESCRIPTION}
  </div>
);

const hasAnyImpactedLocation = locations =>
  !!locations && Object.values(locations).some(Boolean);

const getFacilityDisplayValue = item => {
  if (!item?.issuingFacility) {
    return undefined;
  }

  return isOtherMedicalFacility(item)
    ? item?.issuingFacilityOther
    : getVhaMedicalFacilityLabel(item?.issuingFacility);
};

const getImpactedLocationDisplayValue = impactedLocations => {
  if (!impactedLocations) {
    return undefined;
  }

  const selectedLabels = Object.entries(impactedLocations)
    .filter(([, selected]) => selected)
    .map(([key]) => BODY_PART_LABELS[key])
    .filter(Boolean);

  return selectedLabels.length ? selectedLabels.join(', ') : undefined;
};

export const arrayBuilderOptions = {
  arrayPath: deviceDetails.ARRAY_PATH,
  nounSingular: deviceDetails.NOUN_SINGULAR,
  nounPlural: deviceDetails.NOUN_PLURAL,
  required: true,
  isItemIncomplete: item =>
    !item?.serviceConnectedDisability ||
    !item?.itemType ||
    !item?.issuingFacilityState ||
    !getVhaMedicalFacilityLabel(item?.issuingFacility) ||
    (isOtherMedicalFacility(item) && !item?.issuingFacilityOther?.trim()) ||
    !hasAnyImpactedLocation(item?.impactedLocations),
  maxItems: MAX_BENEFIT_ITEMS,
  text: {
    getItemName: item => item?.itemType,
    cardDescription: item => {
      const facility = getFacilityDisplayValue(item);
      const impactedLocations = getImpactedLocationDisplayValue(
        item?.impactedLocations,
      );

      return [item?.serviceConnectedDisability, facility, impactedLocations]
        .filter(Boolean)
        .join(' • ');
    },
    summaryTitle: deviceDetails.SUMMARY_TITLE,
  },
};

const introPage = {
  uiSchema: {
    ...titleUI(deviceDetails.INTRO_TITLE, deviceDetails.INTRO_DESCRIPTION),
  },
  schema: {
    type: 'object',
    properties: {},
  },
};

const summaryPage = {
  uiSchema: {
    'view:hasDeviceApplianceMedicationItems': {
      ...arrayBuilderYesNoUI(arrayBuilderOptions),
      'ui:errorMessages': {
        required: deviceDetails.ADD_ANOTHER_ITEM_REQUIRED,
      },
    },
  },
  schema: {
    type: 'object',
    properties: {
      'view:hasDeviceApplianceMedicationItems': arrayBuilderYesNoSchema,
    },
    required: ['view:hasDeviceApplianceMedicationItems'],
  },
};

export const itemPage = {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: deviceDetails.ITEM_DETAILS_TITLE,
      nounSingular: arrayBuilderOptions.nounSingular,
    }),
    'ui:description': ItemDetailsDescription,
    'ui:validations': [validateOtherMedicalFacility],

    serviceConnectedDisability: {
      ...textUI({
        title: deviceDetails.SERVICE_CONNECTED_DISABILITY_TITLE,
        errorMessages: {
          required: deviceDetails.SERVICE_CONNECTED_DISABILITY_REQUIRED,
        },
      }),
      'ui:options': {
        classNames: 'vads-u-margin-top--0 vads-u-margin-bottom--3',
      },
    },

    itemType: {
      ...textUI({
        title: deviceDetails.ITEM_TYPE_TITLE,
        errorMessages: {
          required: deviceDetails.ITEM_TYPE_REQUIRED,
        },
      }),
      'ui:options': {
        classNames: 'vads-u-margin-bottom--3',
      },
    },

    issuingFacilityState: selectUI({
      title: deviceDetails.ISSUING_FACILITY_STATE_TITLE,
      hint: deviceDetails.ISSUING_FACILITY_STATE_HINT,
      labels: VHA_FACILITY_STATE_LABELS,
      required: () => true,
      classNames: 'vads-u-margin-bottom--3',
      errorMessages: {
        required: deviceDetails.ISSUING_FACILITY_STATE_ERROR,
      },
    }),

    issuingFacility: {
      'ui:title': deviceDetails.ISSUING_FACILITY_TITLE,
      'ui:field': VhaFacilityAutosuggestField,
      'ui:required': () => true,
      'ui:errorMessages': {
        required: deviceDetails.ISSUING_FACILITY_ERROR,
      },
      'ui:options': {
        classNames: 'vads-u-margin-top--3 vads-u-margin-bottom--3',
        hint: deviceDetails.ISSUING_FACILITY_HINT,
        placeholder: 'Select a facility',
        maxOptions: 150,
        getOptions: getVhaMedicalFacilityOptions,
        updateSchema: updateIssuingFacilitySchema,
        useAddressFallback: false,
      },
      'ui:confirmationField': ({ formData }) => ({
        label: deviceDetails.ISSUING_FACILITY_TITLE,
        data: getVhaMedicalFacilityLabel(formData),
      }),
    },

    issuingFacilityOther: {
      ...textUI(VHA_MEDICAL_FACILITY.OTHER_LABEL),
      'ui:options': {
        expandUnder: 'issuingFacility',
        expandUnderCondition: VHA_MEDICAL_FACILITY.OTHER_OPTION,
        classNames: 'vads-u-margin-bottom--3',
      },
      'ui:required': (formDataOrItem, index) => {
        const item = getArrayBuilderItem(formDataOrItem, index);
        return isOtherMedicalFacility(item);
      },
      'ui:errorMessages': {
        required: deviceDetails.ISSUING_FACILITY_OTHER_ERROR,
      },
    },

    impactedLocations: checkboxGroupUI({
      title: deviceDetails.IMPACTED_LOCATIONS_TITLE,
      required: true,
      labels: BODY_PART_LABELS,
      errorMessages: {
        required: deviceDetails.IMPACTED_LOCATIONS_REQUIRED,
      },
    }),
  },
  schema: {
    type: 'object',
    required: [
      'serviceConnectedDisability',
      'itemType',
      'issuingFacilityState',
      'issuingFacility',
      'impactedLocations',
    ],
    properties: {
      serviceConnectedDisability: {
        ...textSchema,
        maxLength: 255,
      },
      itemType: {
        ...textSchema,
        maxLength: 255,
      },
      issuingFacilityState: selectSchema(VHA_FACILITY_STATE_VALUES),
      issuingFacility: { type: 'string' },
      issuingFacilityOther: {
        ...textSchema,
        maxLength: 255,
      },
      impactedLocations: checkboxGroupSchema(Object.keys(BODY_PART_LABELS)),
    },
  },
};

const deviceApplianceMedicationPages = arrayBuilderPages(
  arrayBuilderOptions,
  pageBuilder => ({
    deviceApplianceMedicationIntroPage: pageBuilder.introPage({
      title: deviceDetails.INTRO_PAGE_TITLE,
      path: deviceDetails.INTRO_PATH,
      uiSchema: introPage.uiSchema,
      schema: introPage.schema,
    }),
    deviceApplianceMedicationSummaryPage: pageBuilder.summaryPage({
      title: deviceDetails.SUMMARY_TITLE,
      path: deviceDetails.SUMMARY_PATH,
      uiSchema: summaryPage.uiSchema,
      schema: summaryPage.schema,
    }),
    deviceApplianceMedicationItemPage: pageBuilder.itemPage({
      title: deviceDetails.ITEM_PAGE_TITLE,
      path: deviceDetails.ITEM_PATH,
      uiSchema: itemPage.uiSchema,
      schema: itemPage.schema,
      updateFormData: updateDeviceItemFormData,
    }),
  }),
);

export default deviceApplianceMedicationPages;
